// The figures that decide a scenario, led by its status: GREEN never goes negative, AMBER borrows
// within the facility at a positive margin, RED breaches the facility or loses money.
//
// The palette has no green and no amber (brand guidelines §3.1.3), so the status is carried the way
// the counterparty states are: the strong state in navy, the qualified one on the wash with a
// dashed rule, and the failing one in the error ink - words first, never colour alone. A negative
// figure is bracketed, not tinted.

import { day, money } from './format.js';

// A short list of empty fields is shown open; a long one - an older scenario - folded.
const EMPTY_LISTED_OPEN = 8;

const STATUS_TEXT = {
  GREEN: 'Cash-positive throughout',
  AMBER: 'Needs funding within the facility',
  RED: 'Breaches the facility or loses money',
};

function Tile({ label, value, note }) {
  return (
    <div className="econ-tile">
      <span className="econ-tile-label">{label}</span>
      <strong>{value}</strong>
      {note && <span className="econ-tile-note">{note}</span>}
    </div>
  );
}

// The tiles take the left in three columns; `beside` is the panel set to their right.
export default function Metrics({ result, beside }) {
  const m = result.metrics;
  const c = result.currency;
  return (
    <>
      <p className="econ-status-line" role="status">
        <span className={`status econ-status-${m.status.toLowerCase()}`}>{m.status}</span>
        {STATUS_TEXT[m.status]}
      </p>
      <div className="econ-top">
        <div className="econ-tiles">
          <Tile label="Net margin" value={money(m.net_margin, c)}
                note={`${money(m.net_margin_per_mt, c, { exact: true })}/MT · ${m.net_margin_pct}%`} />
          <Tile label="Peak funding" value={money(m.peak_exposure, c)}
                note={m.peak_exposure_date ? `on ${day(m.peak_exposure_date)}` : 'never negative'} />
          <Tile label="Days negative" value={m.days_negative} />
          <Tile label="Finance cost" value={money(m.finance_cost, c)} />
          <Tile label="Return on capital"
                value={m.return_on_capital === null ? '—' : `${m.return_on_capital}%`}
                note="net margin ÷ peak funding" />
          <Tile label="Revenue" value={money(m.revenue, c)} />
          <Tile label="Purchase" value={money(m.purchase_cost, c)} />
          <Tile label="Other costs" value={money(m.other_costs, c)} />
          <Tile label="Commission" value={money(m.commission, c)} />
        </div>
        {beside}
      </div>
      {result.empty.length > 0 && (
        <details className="econ-warnings econ-empty" open={result.empty.length <= EMPTY_LISTED_OPEN}>
          <summary>
            {result.empty.length} field{result.empty.length === 1 ? ' has' : 's have'} no value
            and count as nothing
          </summary>
          <ul>{result.empty.map((f) => <li key={f}>{f}</li>)}</ul>
        </details>
      )}
      {result.warnings.length > 0 && (
        <ul className="econ-warnings">
          {result.warnings.map((w) => <li key={w}>{w}</li>)}
        </ul>
      )}
    </>
  );
}
